import { ChangeDetectionStrategy, Component, signal, computed, Inject, OnInit, Signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SlackBot } from '@local/client-contracts';
import { PopupRef, PopupService, UButtonModule, UDropdownModule, UIconModule, UInputComponent, UInputModule } from '@local/ui-infra';
import { SlackBotService } from '@shared/services/slack-bot.service';
import { RouterService } from '@shared/services/router.service';
import { Logger } from '@unleash-tech/js-logger';
import { SlackChannelIdInfoComponent } from '../../../../slack-channel-id-info/slack-channel-id-info.component';
import { take } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { CONTROL_PAGE_PATH } from 'src/app/bar/utils/constants';
import { LogService } from '@shared/services';
import { WorkspacesService } from 'src/app/bar/services';
import { orderBy } from 'lodash';

@UntilDestroy()
@Component({
  selector: 'slack-assistant-create-popup',
  standalone: true,
  templateUrl: './slack-assistant-create-popup.component.html',
  styleUrls: ['./slack-assistant-create-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, UButtonModule, UDropdownModule, UIconModule, UInputModule, TooltipModule],
})
export class SlackAssistantCreateComponent implements OnInit {
  readonly ERROR_MESSAGES: Record<SlackBot.ErrorReason, string> = {
    channelNotFound: 'Channel not found',
    assistantAlreadyExist: 'An assistant already exists for this channel',
    memberNotInChannel: 'You must be a member of this channel to create an Assistant',
    installationNotFound: 'installation Not Found',
  };

  readonly selectedWorkspace = signal<any | null>(null);
  readonly slackChannelID = signal<string | null>(null);
  readonly installationsList = signal<Array<{ label: string; value: SlackBot.InstallationDetails }>>([]);
  readonly isLoadingInstallations = signal(true);
  readonly errorMessage = signal<Record<SlackBot.ErrorReason, string> | string>(null);
  readonly isSubmitting = signal(false);
  readonly step = 1;
  readonly stepsLength = 2;
  readonly continueButtonState = computed(() => {
    if (this.errorMessage() == null && this.isSubmitting()) {
      return 'loading';
    }
    if (this.selectedWorkspace() && this.slackChannelID() && !this.isSubmitting() && !this.errorMessage()) {
      return 'regular';
    }
    return 'disabled';
  });
  readonly CHANNEL_ID_REGEX: RegExp = /^[A-Z0-9]{8,12}$/;
  readonly isOwnerOrAdmin: Signal<boolean>;

  private slackChannelIdInfoRef: PopupRef<SlackChannelIdInfoComponent, any>;
  private logger: Logger;

  inputRef = viewChild<UInputComponent>('channelIdInput');

  constructor(
    @Inject(PopupRef) private popupRef: PopupRef<SlackAssistantCreateComponent, any>,
    private slackBotService: SlackBotService,
    private popupService: PopupService,
    private routerService: RouterService,
    private logService: LogService,
    private workspacesService: WorkspacesService
  ) {
    this.logger = this.logService.scope('SlackAssistantCreateComponent');
    this.isOwnerOrAdmin = toSignal(this.workspacesService.ownerOrAdmin$);
  }

  ngOnInit(): void {
    this.loadInstallations();
  }

  private async loadInstallations(): Promise<void> {
    try {
      const installationResponse = await this.slackBotService.getSlackInstallations();
      const installations = installationResponse.installations.filter(
        (installation) => installation.botType !== 'agent' && (this.isOwnerOrAdmin() || installation.allowMemberToCreateAssistant)
      );

      const formatted = installations.map((i) => ({
        label: `${i.workspaceName} / ${i.botName || i.botType}`,
        value: i,
      }));

      this.installationsList.set(orderBy(formatted, [(item) => item.label]));

      if (formatted.length === 1) {
        this.selectedWorkspace.set(formatted[0]);
      }
    } finally {
      this.isLoadingInstallations.set(false);
    }
  }

  onInstallationSelected(installation: SlackBot.InstallationDetails): void {
    this.selectedWorkspace.set(installation);
    this.resetValidation();
    if (installation) {
      setTimeout(() => {
        this.inputRef().focusInput();
      }, 10);
    }
  }

  onChannelIDChange(id: string): void {
    const trimmedId = id.trim();
    this.slackChannelID.set(trimmedId);
    if (!trimmedId || !this.CHANNEL_ID_REGEX.test(trimmedId)) {
      this.errorMessage.set('Invalid channel ID format');
    } else {
      this.errorMessage.set(null);
    }

    this.isSubmitting.set(false);
  }

  private resetValidation(): void {
    this.isSubmitting.set(false);
    this.errorMessage.set(null);
  }

  openInfoPopup(): void {
    this.slackChannelIdInfoRef = this.popupService.open(
      'center',
      SlackChannelIdInfoComponent,
      {},
      {
        position: 'center',
        backdropStyle: 'blur-2',
        panelClass: 'channel-id-info-popup',
      }
    );

    this.slackChannelIdInfoRef.compInstance.gotEvent.pipe(take(1)).subscribe(() => {
      this.slackChannelIdInfoRef?.destroy();
    });
  }

  async onContinue(): Promise<void> {
    const installation = this.selectedWorkspace().value;
    const channelId = this.slackChannelID();

    if (!installation || !channelId) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    try {
      const res = await this.validateChannelInfo(installation.id, channelId);
      const channelName = res?.channelName || '';
      const isPrivate = res?.isPrivate || false;

      if (!res?.valid) {
        this.errorMessage.set(this.ERROR_MESSAGES[res.error] || 'Unknown error');
        return;
      }

      this.routerService.navigate([CONTROL_PAGE_PATH, 'assistant-settings', 'new', 'slack'], {
        state: {
          channelId,
          channelName,
          isPrivate,
          fromPopup: true,
          installation,
        },
      });
      this.closePopup();
    } catch (error) {
      this.logger.error('Error validating channel info', error);
      this.errorMessage.set(null);
      this.isSubmitting.set(false);
    }
  }

  private async validateChannelInfo(installationId: string, channelId: string): Promise<SlackBot.ChannelInfoResponse> {
    const request: SlackBot.ChannelInfoRequest = { installationId: installationId, channelId: channelId };
    return this.slackBotService.validateChannelInfo(request);
  }

  onCancel(): void {
    this.closePopup();
  }

  private closePopup(): void {
    this.popupRef.destroy();
  }
}
